import React, { useState } from 'react';
import { trackEvent } from '../../utils/analytics';
import '../../styles/components/UpdatePills.css';

/**
 * Small announcement pills in the top-right of the home view.
 *
 * To add an update, put a new entry at the TOP of this list. `id` is
 * what keys the dismissal in localStorage, so a new entry still shows
 * for a visitor who dismissed an earlier one — never reuse an id for
 * different copy, or that visitor will never see the new text.
 *
 * Keep `text` short. The pill is a single line on desktop and wraps to
 * at most two on mobile; anything longer than ~45 characters starts to
 * crowd the viewport edge.
 */
const UPDATES = [
    {
        id: 'aws-ai-practitioner-2026-10',
        tag: 'New',
        text: 'Got my AWS AI Practitioner certification',
    },
];

const STORE_KEY = 'dismissedUpdates';

const readDismissed = () => {
    try {
        const raw = localStorage.getItem(STORE_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
        // Private browsing or blocked storage — show the pills rather
        // than fail, and accept that dismissal won't persist.
        return [];
    }
};

const UpdatePills = () => {
    const [dismissed, setDismissed] = useState(readDismissed);

    const dismiss = (id) => () => {
        const next = [...new Set([...dismissed, id])];
        setDismissed(next);
        try {
            localStorage.setItem(STORE_KEY, JSON.stringify(next));
        } catch (e) { /* storage unavailable; dismissal is session-only */ }
        trackEvent('update_pill_dismissed', { update_id: id });
    };

    const visible = UPDATES.filter((u) => !dismissed.includes(u.id));
    if (!visible.length) return null;

    return (
        <div className="UpdatePills" role="status" aria-live="polite" aria-label="Recent updates">
            {visible.map((u) => (
                <div className="UpdatePill" key={u.id}>
                    <span className="UpdatePill__tag">{u.tag}</span>
                    <span className="UpdatePill__text">{u.text}</span>
                    <button
                        type="button"
                        className="UpdatePill__close"
                        onClick={dismiss(u.id)}
                        aria-label={`Dismiss update: ${u.text}`}
                    >
                        <svg viewBox="0 0 14 14" aria-hidden="true" focusable="false">
                            <path
                                d="M3 3l8 8M11 3l-8 8"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                            />
                        </svg>
                    </button>
                </div>
            ))}
        </div>
    );
};

export default UpdatePills;
